import type { QueryClient } from "@tanstack/react-query";

/**
 * Returns the original reference unchanged on no-match so React Query's
 * setQueryData consumers can skip the change notification.
 */

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function replaceCompletedEntityInCache<T, Entity>(
  data: T,
  completedEntity: Entity,
  matches: (record: Record<string, unknown>, entity: Entity) => boolean,
): T {
  if (Array.isArray(data)) {
    let didChange = false;
    const nextValue = data.map((item) => {
      const nextItem = replaceCompletedEntityInCache(
        item,
        completedEntity,
        matches,
      );
      if (nextItem !== item) {
        didChange = true;
      }
      return nextItem;
    });
    return (didChange ? nextValue : data) as T;
  }

  if (!isRecord(data)) {
    return data;
  }

  if (matches(data, completedEntity)) {
    return {
      ...data,
      ...completedEntity,
    } as T;
  }

  let didChange = false;
  const nextValue: Record<string, unknown> = {};
  Object.entries(data).forEach(([key, value]) => {
    const nextChildValue = replaceCompletedEntityInCache(
      value,
      completedEntity,
      matches,
    );
    if (nextChildValue !== value) {
      didChange = true;
    }
    nextValue[key] = nextChildValue;
  });

  return (didChange ? nextValue : data) as T;
}

/**
 * Walks every cached query, patching it via `replaceCompletedEntityInCache`,
 * but only writes the result when the patcher actually changed the value —
 * preventing an app-wide React Query notification storm on every successful
 * purchase.
 */
export function patchAllQueriesWithCompletedEntity<Entity>(
  queryClient: QueryClient,
  completedEntity: Entity,
  matches: (record: Record<string, unknown>, entity: Entity) => boolean,
): void {
  queryClient.getQueryCache().getAll().forEach((query) => {
    const cached = query.state.data;
    const next = replaceCompletedEntityInCache(cached, completedEntity, matches);
    if (next !== cached) {
      queryClient.setQueryData(query.queryKey, next);
    }
  });
}
